/**
 * MONSTER TOWN — TERRAIN + STREET ASSEMBLY. WORLD CREW (src/world/**).
 *
 * Builds the whole static town into a scene node's `ground` and `statics`
 * layers and hands back the two things the rest of the crew needs:
 *   - `blockers`: rectangles a monster must walk around (nav mask),
 *   - `landmarks`: the doors, the pond and the mailbox a tap can reach.
 *
 * DRAW-CALL DISCIPLINE (older iPad is the target, not a nice-to-have):
 * everything here is built with the shared kit and then run through
 * `mergeStatic()` per cluster, so the whole town is a few dozen draw calls
 * instead of several hundred, while staying frustum-cullable per cluster.
 */

import * as THREE from 'three'
import { css, hex, mix, rgba, type PaletteName } from '../core/palette'
import type { SceneNode } from '../core/scene'
import type { Vec3 } from '../core/types'
import { boxOn, flat, generatedTexture, group, planeGeo, speckleTexture, toon } from '../render/kit'
import { cottage, makeBuilding, type BuiltBuilding, type Rect } from './buildings'
import {
  BUILDINGS,
  COTTAGES,
  GATE_HALF,
  GREEN_BELT_IN,
  GREEN_BELT_OUT,
  GRID_HALF,
  POND,
  ROAD_IN,
  ROAD_OUT,
  WALK_OUT,
  YARD_HALF,
} from './layout'
import { mergeStatic, seededRandom } from './merge'
import {
  bench,
  bush,
  dock,
  flowerBed,
  flowerClump,
  fountain,
  hedge,
  hill,
  lampPost,
  lilyPad,
  mailbox,
  pineTree,
  reeds,
  rock,
  signPost,
  stump,
  tree,
} from './props'

/** Halfway between the two sky palette entries — the mid band of the gradient. */
const PALE_SKY = `#${mix('skyTop', 'skyHorizon', 0.5).toString(16).padStart(6, '0')}`

export interface Landmark {
  readonly id: string
  readonly at: Vec3
  readonly standAt: Vec3
  readonly radius: number
  /** The bit of art that reacts to a tap. */
  readonly art: THREE.Object3D
  readonly toScene?: string
  readonly panel?: string
  /** Toast picture (LAW #4 — never a word). */
  readonly icon: string
  readonly cue: string
  readonly effect: string
}

/**
 * A piece of STANDING SCENERY that answers a tap.
 *
 * LAW #1 says everything you tap does something, and the placeable audit proves
 * it for the things a kid places. It said nothing about the opening screen,
 * where round 1 measured ONE tappable object against a whole town of lamp
 * posts, benches, trees, flowers, signs and crossings that returned nothing but
 * a walk. That is the screen that decides whether a kid keeps playing.
 *
 * Scenery stays merged (an older iPad cannot afford 60 pickable props), so a
 * tap here does not animate the mesh: it walks the monster over, plays the
 * right particles, the right sound, the right monster pose and a picture toast,
 * and — for the lamp posts — actually turns the town's lights on.
 */
export interface SceneryTap {
  readonly id: string
  readonly at: Vec3
  readonly standAt: Vec3
  readonly radius: number
  /** Toast picture (LAW #4 — never a word). */
  readonly icon: string
  readonly cue: string
  readonly effect: string
  /** Pose the monster strikes on arrival. */
  readonly action?: string
  /** Tapping any lamp warms up every lamp in town. */
  readonly lamp?: boolean
}

export interface TownBuild {
  readonly blockers: Rect[]
  readonly buildings: BuiltBuilding[]
  readonly landmarks: Landmark[]
  /** Standing scenery that answers a tap. */
  readonly taps: SceneryTap[]
  /** Every lamp bulb, merged into one mesh, so "lights on" is one material swap. */
  readonly lampGlow: THREE.Mesh | null
  /** Faint snap grid — shown in build mode only. */
  readonly gridHint: THREE.Object3D
  /**
   * Everything PAINTED on the ground: roads, kerbs, crossings, gate paths, the
   * plaza, the corner gardens and the pond's water rings. Split out from the
   * grass under it so `clearTown()` can show the genuinely empty plot — see the
   * note at the `townPaint` group in `buildTown`.
   */
  readonly paint: THREE.Object3D
}

// ---------------------------------------------------------------------------
// flat ground pieces
// ---------------------------------------------------------------------------

/**
 * Textured-ground materials are cached like everything in the kit: two of them
 * exist for the whole game (wild grass, mown lawn) and they are shared.
 */
const groundMats = new Map<string, THREE.Material>()
function groundMaterial(color: PaletteName | number, texture: THREE.Texture): THREE.Material {
  const c = typeof color === 'number' ? color : hex(color)
  const key = `${c}|${texture.uuid}`
  const cached = groundMats.get(key)
  if (cached !== undefined) return cached
  const m = new THREE.MeshToonMaterial({ color: c, map: texture })
  groundMats.set(key, m)
  return m
}

/** A flat rectangle of ground. `y` layers it above whatever is beneath. */
function slab(
  parent: THREE.Object3D,
  x0: number,
  z0: number,
  x1: number,
  z1: number,
  y: number,
  color: PaletteName | number,
  texture?: THREE.Texture,
): void {
  const w = Math.abs(x1 - x0)
  const d = Math.abs(z1 - z0)
  if (w <= 0.001 || d <= 0.001) return
  const mat = texture === undefined ? toon(color) : groundMaterial(color, texture)
  const mesh = new THREE.Mesh(planeGeo(w, d), mat)
  mesh.position.set((x0 + x1) / 2, y, (z0 + z1) / 2)
  mesh.receiveShadow = true
  parent.add(mesh)
}

/**
 * THE SHAPE OF WATER — a smooth superellipse outline, lying flat in XZ.
 *
 * Round 2's pond was three overlapping axis-aligned rectangles per ring ("a
 * stepped, corner-cut ring"), which is exactly what the critic photographed: a
 * chunky ~8px staircase on every ring, and because each ring used a DIFFERENT
 * corner cut, the steps of one ring never lined up with the steps of the next —
 * "five concentric shapes … mis-registered against each other".
 *
 * Real curved geometry fixes both at once. One outline function, one `inset`
 * argument: every ring is the same curve at a different offset, so they are
 * concentric by construction and can never drift apart again.
 */
function ovalGeometry(hx: number, hz: number, inset: number, seg = 56): THREE.BufferGeometry {
  const a = Math.max(0.05, hx + inset)
  const b = Math.max(0.05, hz + inset)
  // n = 3 is halfway between an ellipse (2) and a rounded rectangle (4): a
  // natural pond, not a swimming pool and not a circle.
  const n = 3
  const pts: THREE.Vector2[] = []
  for (let i = 0; i < seg; i++) {
    const t = (i / seg) * Math.PI * 2
    const c = Math.cos(t)
    const s = Math.sin(t)
    pts.push(
      new THREE.Vector2(
        a * Math.sign(c) * Math.pow(Math.abs(c), 2 / n),
        b * Math.sign(s) * Math.pow(Math.abs(s), 2 / n),
      ),
    )
  }
  const geo = new THREE.ShapeGeometry(new THREE.Shape(pts))
  geo.rotateX(-Math.PI / 2)
  return geo
}

/** One smooth ring of the pond, laid flat at `y`. */
function oval(
  parent: THREE.Object3D,
  cx: number,
  cz: number,
  hx: number,
  hz: number,
  inset: number,
  y: number,
  color: PaletteName | number,
): void {
  const mesh = new THREE.Mesh(ovalGeometry(hx, hz, inset), toon(color))
  mesh.position.set(cx, y, cz)
  mesh.receiveShadow = true
  parent.add(mesh)
}

/**
 * A flat annulus of ground — the aerial-perspective bands that turn the far
 * field into a horizon instead of a map that runs out.
 */
function ringBand(
  parent: THREE.Object3D,
  inner: number,
  outer: number,
  y: number,
  color: PaletteName | number,
  receive = false,
  texture?: THREE.Texture,
  /** Radius the texture's repeat was tuned against — see below. */
  uvRadius = 0,
): void {
  const geo = new THREE.RingGeometry(inner, outer, 64, 1)
  geo.rotateX(-Math.PI / 2)
  if (texture !== undefined && uvRadius > 0) {
    /*
     * RingGeometry normalises its UVs against its OWN outer radius, and so does
     * CircleGeometry — so the same texture at the same `repeat` lands at a
     * different fleck size on a 34-unit ring than on the 44-unit field disc, and
     * the boundary reappears as a change of grain. Rescaling the ring's UVs
     * about their centre by outer/uvRadius makes the texel density identical on
     * every piece of ground, whatever radius it happens to be.
     */
    const uv = geo.getAttribute('uv') as THREE.BufferAttribute
    const k = outer / uvRadius
    for (let i = 0; i < uv.count; i++) {
      uv.setXY(i, 0.5 + (uv.getX(i) - 0.5) * k, 0.5 + (uv.getY(i) - 0.5) * k)
    }
    uv.needsUpdate = true
  }
  // TEXTURE IT LIKE THE FIELD IT SITS ON, or it is a different green.
  // A map MULTIPLIES the material colour, so an untextured band and a textured
  // field declared with the SAME colour do not come out the same colour — the
  // band comes out brighter and flat. That is the round-3 defect verbatim: "the
  // ground has a visible soft-edged lighter-green disc under the town with a
  // fuzzy boundary … the terrain reads as a spotlight on a plane rather than
  // land." Nothing was lighting anything; two pieces of the same lawn simply
  // did not share a material.
  const mesh = new THREE.Mesh(geo, texture === undefined ? toon(color) : groundMaterial(color, texture))
  mesh.position.y = y
  mesh.receiveShadow = receive
  mesh.castShadow = false
  parent.add(mesh)
}

/**
 * A soft contact shadow disc. Scenery clusters deliberately do NOT cast into
 * the shadow map (a merged cluster's bounding sphere is huge and is almost
 * never culled out of the shadow pass), which round 2 read as "several trees
 * have no shadow while inner ones do". A painted blob under every scenery prop
 * grounds all of them for a few triangles and no extra pass.
 */
function blobShadow(parent: THREE.Object3D, x: number, z: number, r: number): void {
  const geo = new THREE.CircleGeometry(r, 14)
  geo.rotateX(-Math.PI / 2)
  const mesh = new THREE.Mesh(geo, SHADOW_BLOB)
  mesh.position.set(x, 0.05, z)
  mesh.castShadow = false
  mesh.receiveShadow = false
  parent.add(mesh)
}

/** One shared material for every contact blob — one bucket, one draw call. */
const SHADOW_BLOB = new THREE.MeshBasicMaterial({
  color: hex('shadow'),
  transparent: true,
  opacity: 0.17,
  depthWrite: false,
})

/**
 * The ground's ink line. Not pure `ink` — a black hairline on grass under warm
 * daylight reads as a crack. This is the same family as the props' outlines,
 * softened into the earth tones so it looks drawn rather than cut.
 */
const GROUND_INK = mix('inkSoft', 'dirtDark', 0.42)

/**
 * THE FIELD. Module scope because the exit lanes fade into it and the horizon
 * bands step out from it — three places that must agree on one green.
 */
const FIELD = 44
const FIELD_GREEN = mix('grass', 'sand', 0.16)

/**
 * SNAP A BLEND FACTOR TO A LADDER. This is a DRAW-CALL tool, not an art one.
 *
 * `mergeStatic` buckets by material and `toon()` caches by colour, so a tree
 * tinted with a continuous random blend is a unique material, which is a unique
 * bucket, which is its own draw call. Sixty individually-tinted trees cost sixty
 * draw calls and blew the older-iPad budget the moment the belt got its variety.
 * Quantising the blend to a handful of steps keeps every bit of the variety a
 * kid can see and collapses the belt back into a handful of merged meshes.
 */
function quant(t: number, steps: number): number {
  return Math.round(Math.min(1, Math.max(0, t)) * steps) / steps
}

/** `mix()` for two already-resolved colours. Same maths, no palette lookup. */
function blend(a: number, b: number, t: number): number {
  const k = Math.min(1, Math.max(0, t))
  const r = Math.round(((a >> 16) & 255) * (1 - k) + ((b >> 16) & 255) * k)
  const g = Math.round(((a >> 8) & 255) * (1 - k) + ((b >> 8) & 255) * k)
  const bl = Math.round((a & 255) * (1 - k) + (b & 255) * k)
  return (r << 16) | (g << 8) | bl
}

/** Perceived brightness, 0..255. The only thing that decides whether a shape reads. */
function lum(c: number): number {
  return 0.2126 * ((c >> 16) & 255) + 0.7152 * ((c >> 8) & 255) + 0.0722 * (c & 255)
}

/**
 * ============ A PLANT IS ALWAYS DARKER THAN THE GROUND IT STANDS ON ============
 *
 * MEASURED DEFECT, day AND night, from ONE cause. "Trees render as empty
 * outlines by day and glowing ghosts by night."
 *
 * The belt's six leaf shades included `leafLight` (#86D06A, luminance 185) and
 * `mix('leafLight','flowerYellow',0.2)` (#9ED167, luminance 190) — and the field
 * they are planted on is `FIELD_GREEN` at luminance 175. Two of the six shades
 * were therefore BRIGHTER than the grass by ten to fifteen points of luminance
 * at a near-identical hue, which is inside the noise of a toon band. Those trees
 * had no silhouette at all: only their ink shell reached the eye, which is
 * exactly the reported "an INK OUTLINE ONLY with the grass showing straight
 * through it".
 *
 * The same arithmetic is why they glow at night. The night grade is a
 * full-screen MULTIPLY (`src/time/tint.ts`), so it scales every surface by the
 * same factor and preserves ratios: a tree that is 1.08x the grass at noon is
 * still 1.08x the grass at midnight, and against a dark field that reads as a
 * lamp. Re-mixing the tint against the clock — the obvious fix — would not have
 * touched it, because the fault was never the mix, it was the ORDERING.
 *
 * So the ordering is now guaranteed rather than hoped for: every scattered
 * plant's base colour is pushed down until it is a clear step below the ground
 * immediately behind it, keeping its hue. Variety survives (a light warm green
 * is still lighter than a deep one); "lighter than the grass" does not. Because
 * it is a RATIO, one static tint holds at every hour of the day — no per-frame
 * work, nothing to re-bake when the clock moves.
 */
/**
 * How far below its own backdrop a plant's BASE colour is held. 0.74 is not a
 * taste number: the toon material lights the sun-facing faces of a crown back UP
 * by roughly a sixth, so a base at 0.8 came out on screen within ten points of
 * the grass on the top face and the silhouette still only half read. Measured on
 * the captures, not guessed — compare `shots/fix-int-r1-before/zoom-day-treeline`
 * with `shots/fix-int-r1-after/day-treeline`.
 */
const FOLIAGE_HEADROOM = 0.74
function underGround(c: number, ground: number): number {
  const cap = lum(ground) * FOLIAGE_HEADROOM
  const l = lum(c)
  if (l <= cap || l <= 0.001) return c
  const k = cap / l
  return (
    (Math.round(((c >> 16) & 255) * k) << 16) |
    (Math.round(((c >> 8) & 255) * k) << 8) |
    Math.round((c & 255) * k)
  )
}

/**
 * What the painted ground looks like at a given radius, so the skyline can be
 * held below its OWN backdrop rather than below the near field. Mirrors the band
 * ladder in `paintGround` — the two are read off the same shape of curve, and
 * the only thing that matters is that this never says "lighter than it is".
 */
function groundAt(radius: number): number {
  const haze =
    radius <= FIELD ? 0.02
    : radius <= 53 ? 0.16
    : radius <= 64 ? 0.33
    : radius <= 80 ? 0.6
    : 0.9
  return blend(FIELD_GREEN, hex('skyHorizon'), haze)
}

/**
 * A paved rectangle WITH A KERB.
 *
 * THE STAIRCASE FIX. The renderer runs without MSAA (deliberate: fill rate on
 * an older iPad) and can drop below 1.0 render scale, so a hard boundary
 * between two flat colours — bright grass against bright path — resolves as a
 * visible pixel staircase along every isometric diagonal. Round 1's paths were
 * exactly that: one colour meeting another with nothing in between.
 *
 * Every paved surface now lays a `pathEdge` kerb slab a little wider and a hair
 * lower, so the eye crosses grass -> kerb -> path in two smaller steps instead
 * of one big one. Two steps of half the contrast read as a soft edge, and the
 * kerb is also just what a real path has.
 */
function paved(
  parent: THREE.Object3D,
  x0: number,
  z0: number,
  x1: number,
  z1: number,
  y: number,
  color: PaletteName,
  kerb = 0.34,
): void {
  /*
   * AND AN INK LINE UNDER THE KERB.
   *
   * The kerb alone was not enough: round 2 read "the house and the player have
   * a dark outline; the roads, kerbs and grass have none … the UI and the world
   * look like two different products". Ground is flat, so it cannot carry an
   * inverted-hull outline like a prop — it gets a drawn one instead. A 7cm
   * `inkSoft` band under the kerb gives every paved edge the same soft ink line
   * the props and the HUD icons have, and a dark line between two mid tones is
   * also the cheapest anti-aliasing there is: the eye stops seeing the staircase
   * once the step is dark-to-light rather than light-to-light.
   */
  const ink = kerb + 0.075
  slab(parent, x0 - ink, z0 - ink, x1 + ink, z1 + ink, y - 0.006, GROUND_INK)
  slab(parent, x0 - kerb, z0 - kerb, x1 + kerb, z1 + kerb, y - 0.004, 'pathEdge')
  slab(parent, x0, z0, x1, z1, y, color)
}

/**
 * A LANE THAT LEAVES TOWN.
 *
 * ROUND-3: "the roads simply stop at the town boundary with a hard cut edge …
 * instead of fading, curving away, or meeting anything." The ring road was a
 * closed loop with nothing joining it, so wherever the frame cut across the
 * pavement's outer kerb the town read as a diorama on a lawn.
 *
 * Each lane runs out from the pavement, narrows, and washes from tarmac to
 * field green in three steps until there is nothing left to end — it dissolves
 * into the tree belt instead of stopping at a line. The first step is `paved()`,
 * so it carries the same kerb and drawn ink edge as every other road in town.
 *
 * There are THREE of them, at three different offsets, deliberately: four
 * evenly-placed exits would put the symmetry straight back that the corner
 * gardens gave up.
 */
function exitLane(
  parent: THREE.Object3D,
  blockers: Rect[],
  axis: 'x' | 'z',
  /** Position along the side the lane leaves from. */
  offset: number,
  /** Which way out: -1 toward the negative axis, +1 toward the positive. */
  dir: -1 | 1,
): void {
  const start = WALK_OUT + 0.35
  /*
   * IT NARROWS AND CHANGES SURFACE, IT DOES NOT DISSOLVE INTO THE LAWN.
   *
   * First attempt faded the tarmac toward the field green with plain untinted
   * slabs — which is the SAME mistake as the apron ring: the field carries a
   * speckle map, so an untextured slab of "the field's colour" comes out paler
   * and reads as a ghost rectangle lying on the grass. Every step is now an
   * existing town surface (tarmac, then a path, then a dirt track) laid with
   * `paved()`, so each one carries the kerb and the drawn ink edge that the rest
   * of the town's ground has, and none of it has to match the grass at all.
   *
   * Each step also steps SIDEWAYS, so the lane bends away instead of running
   * dead straight to a stop, and the last one is narrow enough for the tree belt
   * to close over — which is what "meeting something" looks like from a fixed
   * camera that cannot follow it.
   */
  // [length, half width, sideways offset, surface, kerb]. Each step reaches BACK
  // over the one before it (`OVERLAP`) so the kerbs meet and the lane reads as
  // one road that narrows, not as three paving slabs laid end to end.
  const OVERLAP = 0.9
  const segs: readonly [number, number, number, PaletteName, number][] = [
    [4.6, 1.8, 0, 'road', 0.32],
    [4.4, 1.4, 0.7, 'path', 0.28],
    [4.0, 0.95, 1.7, 'dirt', 0.24],
  ]
  let at = start
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i]
    if (s === undefined) continue
    const [len, half, side, color, kerb] = s
    const from = i === 0 ? at : at - OVERLAP
    const a = dir * from
    const b = dir * (at + len)
    const lo = Math.min(a, b)
    const hi = Math.max(a, b)
    const c = offset + side * dir
    if (axis === 'x') paved(parent, c - half, lo, c + half, hi, 0.045, color, kerb)
    else paved(parent, lo, c - half, hi, c + half, 0.045, color, kerb)
    at += len
  }
  /*
   * Keep the tree scatter off the lane — but only off the first two thirds of
   * it. The dirt track deliberately keeps NO clearance, so the belt grows right
   * over where it ends and the track goes into the woods rather than stopping in
   * the open. Everything out here is already outside the walkable grid, so none
   * of this changes anything a monster can do.
   */
  const keep = start + (segs[0]?.[0] ?? 0) + (segs[1]?.[0] ?? 0)
  const lo = Math.min(dir * (start - 0.5), dir * keep)
  const hi = Math.max(dir * (start - 0.5), dir * keep)
  blockers.push(
    axis === 'x'
      ? { x0: offset - 2.6, z0: lo, x1: offset + 2.6, z1: hi }
      : { x0: lo, z0: offset - 2.6, x1: hi, z1: offset + 2.6 },
  )
}

/** A square annulus (the ring road and its pavements) as four bands. */
function annulus(
  parent: THREE.Object3D,
  inner: number,
  outer: number,
  y: number,
  color: PaletteName | number,
): void {
  slab(parent, -outer, -outer, outer, -inner, y, color)
  slab(parent, -outer, inner, outer, outer, y, color)
  slab(parent, -outer, -inner, -inner, inner, y, color)
  slab(parent, inner, -inner, outer, inner, y, color)
}

/**
 * Dashed centre line down the middle of each road band, WITH A JUNCTION.
 *
 * Round 2: "road centre dashes are irregular, some overlap the intersections,
 * and there is no junction treatment where two roads meet". Both come from the
 * same bug — the dash loop started at a fixed `-span` and stepped by a fixed
 * period, so the last dash before a corner landed wherever the arithmetic put
 * it, including inside the corner where the two roads cross.
 *
 * The run is now CENTRED on each side and a whole number of periods long, and
 * it stops clear of both the corner boxes and the four zebra crossings. Where
 * two roads meet there is now a real junction: no centre line at all, which is
 * exactly what a real crossroads looks like.
 */
function roadMarkings(parent: THREE.Object3D, y: number): void {
  const mid = (ROAD_IN + ROAD_OUT) / 2
  const dash = 2.0
  const period = 4.0
  const half = 0.14
  // Clear of the corner (the road is ROAD_OUT-ROAD_IN wide, so the junction box
  // reaches ROAD_IN in from each end) and clear of the crossing at the middle.
  const limit = ROAD_IN - 0.6
  const crossClear = GATE_HALF + 1.4
  const runs: number[] = []
  // Two symmetric runs per side: corner -> crossing, crossing -> corner.
  const count = Math.max(1, Math.floor((limit - crossClear) / period))
  for (let i = 0; i < count; i++) {
    const start = crossClear + i * period + (period - dash) / 2
    runs.push(start, -start - dash)
  }
  for (const t of runs) {
    slab(parent, t, -mid - half, t + dash, -mid + half, y, 'flowerWhite')
    slab(parent, t, mid - half, t + dash, mid + half, y, 'flowerWhite')
    slab(parent, -mid - half, t, -mid + half, t + dash, y, 'flowerWhite')
    slab(parent, mid - half, t, mid + half, t + dash, y, 'flowerWhite')
  }
  // The junction itself: a soft paler square in each corner so the crossroads
  // reads as a place where the two roads join rather than as a gap in the paint.
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      slab(
        parent,
        sx * ROAD_IN,
        sz * ROAD_IN,
        sx * ROAD_OUT,
        sz * ROAD_OUT,
        y - 0.004,
        mix('road', 'pathEdge', 0.22),
      )
    }
  }
}

/**
 * Lawn texture.
 *
 * NO STRIPES. Round 1 drew two hard horizontal bands of a second green across
 * every texture tile and then repeated it 4x4 over the plot, which produced the
 * exact defect the critic named: "strong horizontal light/dark banding and
 * per-tile checkerboarding … reads as a tiled texture with a UV seam artifact,
 * not as grass". A repeating texture on a big flat plane must have NO feature
 * bigger than a tile and NO axis-aligned edge, or the tiling is the pattern.
 *
 * What is left is isotropic: hundreds of tiny grass flecks at low alpha, drawn
 * with wrap-around so nothing is clipped at the tile border. At the plot's
 * repeat that is sub-centimetre detail — texture, not pattern.
 */
function lawnTexture(): THREE.Texture {
  return generatedTexture('lawn:soft', (c, s) => {
    c.fillStyle = css('grassLight')
    c.fillRect(0, 0, s, s)
    let seed = 9182
    const rnd = (): number => {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0
      return seed / 0x100000000
    }
    // Two flecks passes, one lighter one darker, so the average stays put and
    // the plane never reads as two tones of green in bands.
    for (const [color, alpha, count] of [
      ['grass', 0.16, 260],
      ['grassDark', 0.1, 170],
      ['leafLight', 0.12, 140],
    ] as const) {
      c.fillStyle = rgba(color, alpha)
      for (let i = 0; i < count; i++) {
        const x = rnd() * s
        const y = rnd() * s
        const w = 0.8 + rnd() * 1.6
        const h = 1.4 + rnd() * 2.4
        const a = rnd() * Math.PI
        // Wrapped copies keep the fleck field seamless across the tile edge.
        for (const dx of [-s, 0, s]) {
          for (const dy of [-s, 0, s]) {
            if ((dx !== 0 || dy !== 0) && (x > w * 2 && x < s - w * 2 && y > h * 2 && y < s - h * 2)) {
              continue
            }
            c.beginPath()
            c.ellipse(x + dx, y + dy, w, h, a, 0, Math.PI * 2)
            c.fill()
          }
        }
      }
    }
  })
}

/**
 * A little paved square at the middle of the plot. Flat, walkable and fully
 * buildable-over — it costs a kid nothing and gives the town a heart to stand
 * in, which an empty lawn does not.
 */
function plaza(parent: THREE.Object3D): void {
  const r = 4.5
  // Four paths joining the plaza to the four gates, kerbed. Drawn FIRST so the
  // plaza's own kerb lands on top of them and the joins are clean.
  const pw = 1.15
  for (const s of [-1, 1]) {
    paved(parent, -pw, Math.min(s * r, s * YARD_HALF), pw, Math.max(s * r, s * YARD_HALF), 0.014, 'path')
    paved(parent, Math.min(s * r, s * YARD_HALF), -pw, Math.max(s * r, s * YARD_HALF), pw, 0.014, 'path')
  }
  paved(parent, -r, -r, r, r, 0.018, 'path', 0.72)

  /*
   * A compass rose in the middle, so "left is left" reads even after a turn.
   *
   * NO GOLD TILE. Round 1 put a `uiCoin` square at the centre of the rose and
   * the critic read it — fairly — as "a lone gold diamond tile with no apparent
   * function … reads as a bug", because gold in this game means COINS and
   * nothing else. The rose is now drawn in path tones only: an inlay, not a
   * pickup. The checkerboard is gone too; two beiges alternating at this scale
   * only added to the tiled-texture read.
   */
  slab(parent, -2.4, -0.42, 2.4, 0.42, 0.024, 'pathEdge')
  slab(parent, -0.42, -2.4, 0.42, 2.4, 0.024, 'pathEdge')
  slab(parent, -1.05, -1.05, 1.05, 1.05, 0.026, 'pathEdge')
  slab(parent, -0.78, -0.78, 0.78, 0.78, 0.028, 'sand')
}

/**
 * ===========================================================================
 * A BENCH IS SOMETHING YOU SIT *ON*, NOT SOMETHING YOU STAND *IN*
 * ===========================================================================
 * Every bench in the town — two on the plaza, four on the park strip, one by
 * the pond — was built, shadowed and given a tap, and then never added to
 * `blockers`. So the nav mask had never heard of one, and a neighbour (or the
 * kid's own monster) could come to rest with two metres of timber drawn through
 * its middle. Photographed repeatedly:
 * `shots/critic-vr2-probe/kerb-12h.png` (a purple neighbour planted in the
 * bench beside the shop) and `shots/critic-vr2-scenes/16-a-bug-at-night.png`
 * (two of them in the fountain benches).
 *
 * That is the LAST case of the "nobody is in the fountain" work. The routine
 * crew gave a monster a body and asks the mask with a whole body's clearance,
 * and none of that can help against a rectangle the mask does not contain.
 *
 * `bench()` in `props.ts` is 2.3 across and 1.0 deep (the tilted back board
 * sits inside that depth), so the half-extents below are the seat itself. They
 * are rotated into an axis-aligned box because `Rect` is axis-aligned and every
 * bench in the town is placed on a quarter turn — this is exact for all seven,
 * and a safe over-estimate for any angle a later data drop picks.
 */
const BENCH_HX = 1.15
const BENCH_HZ = 0.5

function benchBlocker(x: number, z: number, yaw: number): Rect {
  const c = Math.abs(Math.cos(yaw))
  const s = Math.abs(Math.sin(yaw))
  const hx = BENCH_HX * c + BENCH_HZ * s
  const hz = BENCH_HX * s + BENCH_HZ * c
  return { x0: x - hx, z0: z - hz, x1: x + hx, z1: z + hz }
}

/**
 * The plaza's focal point. Round 2: "the town centre plaza is a large bare tan
 * cross with no fountain, plaza feature or focal point". A fountain is the
 * cheapest thing in the world-building vocabulary that says "this is the middle
 * of somewhere" — and it answers a tap, so it is not decoration (LAW #1).
 */
function plazaFountain(statics: THREE.Object3D, blockers: Rect[], taps: SceneryTap[]): void {
  const g = group('plazaFountain')
  /*
   * OFF THE EXACT CENTRE, DELIBERATELY. The plot's middle cell is where the
   * monster spawns and where `townCenter()` puts the camera, and a solid nav
   * blocker there would trap a brand-new save inside the fountain. Sitting it in
   * one quarter of the plaza also breaks the four-fold symmetry the critic read
   * as machine-generated, and leaves the compass rose readable.
   */
  const fx = -2.9
  const fz = 2.9
  const f = fountain()
  f.position.set(fx, 0, fz)
  g.add(f)
  blobShadow(g, fx, fz, 2.3)
  const seats: readonly [number, number, number][] = [
    [fx + 3.3, fz - 0.4, -Math.PI / 2],
    [fx - 0.4, fz - 3.3, 0],
  ]
  for (let i = 0; i < seats.length; i++) {
    const s = seats[i]
    if (s === undefined) continue
    const [bx, bz, yaw] = s
    const b = bench()
    b.position.set(bx, 0, bz)
    b.rotation.y = yaw
    g.add(b)
    blobShadow(g, bx, bz, 1.3)
    // Solid: see `benchBlocker`. The seat is 1.6 units from `standAt` below, so
    // the spot a monster walks to in order to sit is still on open ground.
    blockers.push(benchBlocker(bx, bz, yaw))
    taps.push({
      id: `bench:plaza${i}`,
      at: { x: bx, y: 0.6, z: bz },
      standAt: { x: bx + Math.sin(yaw) * 1.6, y: 0, z: bz + Math.cos(yaw) * 1.6 },
      radius: 2.0,
      icon: 'furniture',
      cue: 'bench_creak',
      effect: 'poof',
      action: 'sit',
    })
  }
  statics.add(mergeStatic(g, 'plazaFountain', { castShadow: false }))
  blockers.push({ x0: fx - 2.1, z0: fz - 2.1, x1: fx + 2.1, z1: fz + 2.1 })
  taps.push({
    id: 'fountain',
    at: { x: fx, y: 1.4, z: fz },
    standAt: { x: fx + 3.0, y: 0, z: fz },
    radius: 2.9,
    icon: 'water',
    cue: 'splash',
    effect: 'splash',
    action: 'wave',
  })
}

/**
 * PAINTED FLOWER BEDS — IN THE TOWN'S CORNERS, NOT IN THE KID'S YARD.
 *
 * Round 1 put these four beds inside the fence, in the corners of the kid's own
 * four lawns, to stop the plot reading as "four large blank lawns". It worked
 * on that complaint and caused a worse one: on turn one the fenced plot was
 * already decorated in the town's own decorating language, so a brand new save
 * opened on a finished-looking place with nothing of the kid's anywhere in it,
 * and the only obviously free ground was the paved cross at the centre. The
 * kid's canvas has to LOOK like a canvas, or building has no invitation.
 *
 * So the beds moved OUT — to the four corners between the ring road and the
 * tree line, which were bare grass and are the only large empty areas left once
 * the plot belongs to the kid. Same paint, same cost, same "the town is a place
 * where somebody gardens" read; none of it inside the fence.
 *
 * They are still FLAT GROUND at 2cm, with no height and no blocker, so a kid who
 * wants to build over one simply does.
 */
function cornerGardens(parent: THREE.Object3D, blockers: Rect[], taps: SceneryTap[]): void {
  /*
   * AND THEY ARE BUILT, NOT PAINTED.
   *
   * Round 2 still drew each bed as "a brown diamond with four to eight flat
   * pink/yellow/purple squares painted on it, zero thickness, zero geometry, no
   * stems, no shadow" — sitting in the same frame as the fully modelled window
   * boxes on the houses, so the world contradicted itself about what a flower
   * is. `flowerBed()` builds the same idea out of the same vocabulary as the
   * planters: timber edging, a mound of soil, stems, blooms and leaves, with the
   * standard ink outline and a contact shadow.
   *
   * And they are NOT one-per-corner in perfect symmetry any more, which is the
   * other half of the same note ("placed one-per-lawn in perfect symmetry, which
   * makes the town read machine-generated"). Each corner gets a different
   * number of beds, at different sizes, rotations and colour mixes, and one
   * corner gets a bench-and-bush arrangement instead.
   */
  const rnd = seededRandom(3141)
  const R = (WALK_OUT + GRID_HALF) / 2
  const palettes: readonly (readonly PaletteName[])[] = [
    ['flowerPink', 'flowerWhite', 'flowerRed'],
    ['flowerYellow', 'flowerWhite'],
    ['flowerPurple', 'flowerPink', 'flowerYellow'],
  ]
  const corners: readonly [number, number][] = [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
  ]
  for (let ci = 0; ci < corners.length; ci++) {
    const c = corners[ci]
    if (c === undefined) continue
    const [sx, sz] = c
    const beds = 1 + (ci % 3 === 0 ? 2 : ci % 2)
    for (let i = 0; i < beds; i++) {
      const size = 1.9 + rnd() * 1.5
      const bx = sx * (R - 1.2 + rnd() * 4.2) + (i - 0.5) * 2.9 * (i % 2 === 0 ? 1 : -1)
      const bz = sz * (R - 1.2 + rnd() * 4.2) - (i - 0.5) * 2.2
      const colors = palettes[Math.floor(rnd() * palettes.length)] ?? palettes[0]
      const bed = flowerBed(rnd(), colors, size)
      bed.position.set(bx, 0, bz)
      bed.rotation.y = rnd() * 0.6 - 0.3
      parent.add(bed)
      blobShadow(parent, bx, bz, size * 0.62)
      blockers.push({
        x0: bx - size / 2,
        z0: bz - size / 2,
        x1: bx + size / 2,
        z1: bz + size / 2,
      })
      // LAW #1 reaches the garden too: every bed answers a tap with petals.
      taps.push(flowerTap(`bloom:bed${ci}:${i}`, bx, bz, 0, -sz))
    }
    // A shrub or two per corner, so a bed is never the only thing there.
    for (let i = 0; i < 2; i++) {
      const bx = sx * (R + 1.5 + rnd() * 3)
      const bz = sz * (R + 1.5 + rnd() * 3)
      const sh = bush(rnd())
      sh.position.set(bx, 0, bz)
      parent.add(sh)
      blobShadow(parent, bx, bz, 0.85)
    }
  }
}

/**
 * Zebra crossing at each gate so the way out of the plot is obvious.
 *
 * INSET FROM BOTH KERBS. Round 2: "the crosswalk stripes extend past the road
 * edge and are drawn over the sidewalk kerb". They ran the full ROAD_IN..
 * ROAD_OUT span, which is the road's exact extent — and because the crossing is
 * drawn ABOVE the kerb bands and the gate path's own kerb overlaps the road
 * edge, a stripe that reaches the very edge lands on top of the kerb. A real
 * crossing stops short of the kerb anyway; 0.3 of clearance at each end both
 * fixes the overrun and looks right.
 */
function crossings(parent: THREE.Object3D, y: number): void {
  const bars = 5
  const step = (GATE_HALF * 2) / bars
  const near = ROAD_IN + 0.3
  const far = ROAD_OUT - 0.3
  for (let i = 0; i < bars; i++) {
    const a = -GATE_HALF + i * step + step * 0.15
    const b = a + step * 0.7
    slab(parent, a, -far, b, -near, y, 'flowerWhite')
    slab(parent, a, near, b, far, y, 'flowerWhite')
    slab(parent, -far, a, -near, b, y, 'flowerWhite')
    slab(parent, near, a, far, b, y, 'flowerWhite')
  }
}

// ---------------------------------------------------------------------------
// the plot fence
// ---------------------------------------------------------------------------

function plotFence(parent: THREE.Object3D, blockers: Rect[]): void {
  const g = group('plotFence')
  const t = 0.5
  const post = (x: number, z: number): void => {
    g.add(boxOn(0.34, 1.05, 0.34, { color: 'wallCream', x, z }))
  }
  const run = (x0: number, z0: number, x1: number, z1: number): void => {
    const horizontal = Math.abs(x1 - x0) > Math.abs(z1 - z0)
    const len = horizontal ? x1 - x0 : z1 - z0
    if (Math.abs(len) < 0.2) return
    const cx = (x0 + x1) / 2
    const cz = (z0 + z1) / 2
    g.add(
      boxOn(horizontal ? Math.abs(len) : t, 0.62, horizontal ? t : Math.abs(len), {
        color: 'wallCream',
        x: cx,
        y: 0.3,
        z: cz,
      }),
    )
    const n = Math.max(2, Math.round(Math.abs(len) / 2.4))
    for (let i = 0; i <= n; i++) {
      const f = i / n
      post(x0 + (x1 - x0) * f, z0 + (z1 - z0) * f)
    }
    blockers.push({
      x0: Math.min(x0, x1) - (horizontal ? 0 : t),
      z0: Math.min(z0, z1) - (horizontal ? t : 0),
      x1: Math.max(x0, x1) + (horizontal ? 0 : t),
      z1: Math.max(z0, z1) + (horizontal ? t : 0),
    })
  }
  const P = YARD_HALF
  const G = GATE_HALF
  for (const s of [-1, 1]) {
    // north / south runs, split by the gate
    run(-P, s * P, -G, s * P)
    run(G, s * P, P, s * P)
    // west / east runs
    run(s * P, -P, s * P, -G)
    run(s * P, G, s * P, P)
  }
  parent.add(mergeStatic(g, 'plotFence', { castShadow: false }))
}

// ---------------------------------------------------------------------------
// pond
// ---------------------------------------------------------------------------

/**
 * A point ON the pond's outline at angle `t`, offset by `inset`. Everything
 * planted around the pond is placed with this, so a reed can never end up in
 * the grass and a rock can never end up in the middle of the water — the other
 * half of the round-2 pond defect ("several reeds planted in the sand ring or
 * the grass rather than the water").
 */
function pondPoint(t: number, inset: number): { x: number; z: number } {
  const { cx, cz, hx, hz } = POND
  const n = 3
  const c = Math.cos(t)
  const s = Math.sin(t)
  return {
    x: cx + (hx + inset) * Math.sign(c) * Math.pow(Math.abs(c), 2 / n),
    z: cz + (hz + inset) * Math.sign(s) * Math.pow(Math.abs(s), 2 / n),
  }
}

function buildPond(ground: THREE.Object3D, statics: THREE.Object3D, blockers: Rect[]): Landmark {
  const { cx, cz, hx, hz } = POND
  /*
   * ONE CURVE, FIVE OFFSETS.
   *
   * Round 2 built each ring from three overlapping rectangles with a per-ring
   * corner cut, which produced the blocker the critic photographed: a chunky
   * stair-stepped outline on every ring, rings mis-registered against each other
   * so the sand band was thick in one corner and near-zero in another, and a
   * dark `shadow`-coloured inner blob offset toward one side that read as a
   * rendering error rather than deep water.
   *
   * Every ring is now the SAME smooth superellipse at a different inset, so:
   *   - no staircase (real curved geometry, not chamfered rectangles),
   *   - every band is uniform width all the way round, by construction,
   *   - and there is no way to mis-register them, because there is one shape.
   *
   * grass bank -> dirt -> sand shore -> shallow water -> deep water. The dark
   * `shadow` ring is gone; depth is now a small, centred, gently darker inset.
   */
  oval(ground, cx, cz, hx, hz, 2.5, 0.022, mix('grassDark', 'dirt', 0.25))
  oval(ground, cx, cz, hx, hz, 1.75, 0.028, 'dirt')
  oval(ground, cx, cz, hx, hz, 1.0, 0.034, 'sand')
  oval(ground, cx, cz, hx, hz, 0.34, 0.04, mix('sand', 'water', 0.55))
  oval(ground, cx, cz, hx, hz, 0, 0.048, 'water')
  oval(ground, cx, cz, hx, hz, -0.9, 0.054, mix('water', 'waterDeep', 0.6))
  oval(ground, cx, cz, hx, hz, -1.8, 0.06, 'waterDeep')

  const g = group('pondDressing')
  const rnd = seededRandom(7717)
  /*
   * REEDS IN THE WATER, ROCKS ON THE SHORE. Placed against the outline, not
   * against a circle of radius hx — the old code used `cos(a)*(hx+0.9)` for a
   * shape that is not a circle, which is why half of them missed the shore.
   */
  // MARGINAL planting, not a bamboo thicket: eight small clumps hugging the
  // shoreline, with two gaps so the open water stays open and the jetty has a
  // clear run out into it.
  for (let i = 0; i < 8; i++) {
    const t = (i / 8) * Math.PI * 2 + 0.35 + rnd() * 0.18
    if (Math.cos(t) > 0.72) continue
    const v = rnd()
    const p = pondPoint(t, -0.22 - v * 0.28)
    const r = reeds(v)
    r.position.set(p.x, 0.05, p.z)
    r.rotation.y = v * 6.2
    r.scale.setScalar(0.62 + v * 0.22)
    g.add(r)
  }
  /*
   * OUT ON THE BANK, NOT ON THE RIM (vr2). At +0.75 a boulder whose own body is
   * 1.6 units across sat half on the sand ring, and two of the seven were drawn
   * THROUGH the pond edge. The offset is now bigger than the biggest rock.
   */
  for (let i = 0; i < 7; i++) {
    const t = (i / 7) * Math.PI * 2 + 0.4 + rnd() * 0.3
    const v = rnd()
    const p = pondPoint(t, 1.45 + v * 0.5)
    const r = rock(v)
    r.position.set(p.x, r.position.y, p.z)
    g.add(r)
    blobShadow(g, p.x, p.z, 0.6 + v * 0.4)
  }
  // Lily pads: only in OPEN water, and never under the jetty.
  for (let i = 0; i < 7; i++) {
    const t = rnd() * Math.PI * 2
    const p = pondPoint(t, -1.1 - rnd() * 1.4)
    if (Math.abs(p.z - cz) < 1.3 && p.x > cx) continue
    const pad = lilyPad(rnd())
    pad.position.set(p.x, 0.066, p.z)
    g.add(pad)
  }
  /*
   * THE JETTY. Its landward end sits on the sand and its far end reaches into
   * open water, so the pilings are always in water and nothing overhangs the
   * shore. `dock()` now carries pilings that run from below the water line up
   * through the deck, plus a rail whose posts stand on the deck.
   */
  const jettyLen = 5.2
  const jetty = dock(jettyLen)
  jetty.position.set(cx + hx + 0.6 - jettyLen / 2, 0.03, cz)
  jetty.rotation.y = Math.PI / 2
  g.add(jetty)
  const b = bench()
  b.position.set(cx + hx + 3.6, 0, cz - 6)
  b.rotation.y = -Math.PI / 2
  g.add(b)
  blobShadow(g, cx + hx + 3.6, cz - 6, 1.5)
  blockers.push(benchBlocker(cx + hx + 3.6, cz - 6, -Math.PI / 2))
  statics.add(mergeStatic(g, 'pondDressing', { castShadow: false }))

  blockers.push({ x0: cx - hx - 0.6, z0: cz - hz - 0.6, x1: cx + hx - 1.2, z1: cz + hz + 0.6 })

  const splash = group('pondSplash')
  splash.position.set(cx + hx - 2, 0, cz)
  statics.add(splash)
  return {
    id: 'pond',
    at: { x: cx + hx - 2, y: 0.1, z: cz },
    standAt: { x: cx + hx + 2.6, y: 0, z: cz },
    radius: 5,
    art: splash,
    icon: 'water',
    cue: 'splash',
    effect: 'splash',
  }
}

// ---------------------------------------------------------------------------
// scenery scatter
// ---------------------------------------------------------------------------

function hits(x: number, z: number, blockers: readonly Rect[], pad: number): boolean {
  for (const b of blockers) {
    if (x > b.x0 - pad && x < b.x1 + pad && z > b.z0 - pad && z < b.z1 + pad) return true
  }
  return false
}

/**
 * Scenery is merged PER QUADRANT, not into one lump: distance culling in
 * `town.ts` works on cluster centres, and a single town-sized cluster could
 * never be culled.
 */
/** Two halves — see the note in `scatterGreenery`. */
function half(x: number, z: number): number {
  return x + z < 0 ? 0 : 1
}

function scatterGreenery(statics: THREE.Object3D, blockers: Rect[]): void {
  const rnd = seededRandom(20260802)
  /*
   * TWO HALVES, NOT FOUR QUADRANTS.
   *
   * `mergeStatic` buckets by material, so splitting the belt four ways
   * multiplies its bucket count four times over — the same six leaf shades, the
   * same trunk, the same ink and the same shadow material, paid for once per
   * cluster. Four quadrants cost about thirty draw calls more than two halves
   * and only ever cull one extra cluster at the closest zoom, which is the zoom
   * where the belt is off screen anyway. Two halves is the better trade on the
   * hand-me-down iPad, which is the machine that decides.
   */
  const quads = [group('greenery0'), group('greenery1')]
  const far = group('skyline')
  const placed: Rect[] = []
  /*
   * WOODLAND, NOT WALLPAPER.
   *
   * Round 2: "every tree is the same two-cube 'wedding cake' mesh at the same
   * size and colour on a visible regular grid, so the surround reads as a tiling
   * pattern rather than woodland." Two causes, both fixed here:
   *
   *  1. UNIFORM SPACING. A single minimum-distance test at 3.4 units over a
   *     narrow belt IS a lattice — reject-until-it-fits packing converges on
   *     hexagonal order. Trees are now seeded in GROVES: a handful of cluster
   *     centres, dense inside, with real gaps (glades) between them.
   *  2. UNIFORM SIZE AND COLOUR. Every tree came out of `tree(v)` at scale 1
   *     with one of three leaf shades. Scale now runs 0.62..1.45, the leaf shade
   *     is jittered toward the warm end per tree, and stumps and bushes are
   *     mixed in so the belt is not one silhouette repeated.
   */
  const groves: { x: number; z: number; r: number }[] = []
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + rnd() * 0.5
    const rad = GREEN_BELT_IN + 1.5 + rnd() * (GREEN_BELT_OUT - GREEN_BELT_IN - 4)
    groves.push({ x: Math.cos(a) * rad, z: Math.sin(a) * rad, r: 4.5 + rnd() * 4 })
  }
  let tries = 0
  let made = 0
  while (made < 46 && tries < 2600) {
    tries++
    const grove = groves[Math.floor(rnd() * groves.length)]
    if (grove === undefined) continue
    const ga = rnd() * Math.PI * 2
    const gr = Math.sqrt(rnd()) * grove.r
    const x = grove.x + Math.cos(ga) * gr
    const z = grove.z + Math.sin(ga) * gr
    const r = Math.max(Math.abs(x), Math.abs(z))
    // The green belt sits OUTSIDE the grid: nothing here is ever walked to, so
    // it can never get in the way of a kid's tap.
    if (r < GREEN_BELT_IN || r > GREEN_BELT_OUT + 2) continue
    const v = rnd()
    /*
     * A BUSH IS NEVER TREE-SIZED. The scatter used one 0.62..1.45 range for
     * every silhouette, so a shrub could be planted at 1.45 and stand as tall
     * as the tree beside it — half of the round-3 "four different art languages
     * for tree in one screenshot" note. Shrubs get their own tighter band.
     */
    const bushy = v > 0.07 && v <= 0.2
    const scale = v <= 0.07 ? 0.75 + rnd() * 0.3 : bushy ? 0.7 + rnd() * 0.35 : 0.62 + rnd() * 0.83
    // Spacing tracks the actual crown, so big trees keep their room and small
    // ones can tuck in close — which is what stops the field reading as a grid.
    const spread = (v > 0.2 ? 1.9 : v > 0.07 ? 1.15 : 0.7) * scale
    if (hits(x, z, blockers, 2.2) || hits(x, z, placed, spread + 0.5)) continue
    // Six leaf shades, snapped — see `quant`. Six reads as a mixed wood; sixty
    // reads exactly the same and costs sixty draw calls.
    const leafMix = rnd()
    // Six shades, then held below the field they stand on — see `underGround`.
    // The push is applied to the QUANTISED colour, so it cannot invent new
    // materials: six shades in, at most six shades out, same merge buckets.
    const tint = underGround(
      leafMix < 0.34
        ? mix('leaf', 'leafLight', quant(rnd(), 1))
        : leafMix < 0.72
          ? mix('leaf', 'leafDark', quant(rnd() * 0.8, 1))
          : mix('leafLight', 'flowerYellow', 0.2),
      FIELD_GREEN,
    )
    /*
     * THE SCALE GOES IN, IT IS NOT APPLIED AFTERWARDS.
     *
     * `p.scale.setScalar()` here scaled the ink shells with the prop, so a 0.62
     * tree carried a 0.022-unit outline next to a full-size hedge's 0.035 — the
     * round-3 "inconsistent ink outlines in a single frame". Each factory now
     * divides the scale back out of its ink width and applies the scale itself,
     * so every prop in the belt carries the same line at the same screen weight.
     */
    const p =
      v > 0.66
        ? pineTree(rnd(), false, tint, scale)
        : v > 0.22
          ? tree(rnd(), tint, scale)
          : bushy
            ? bush(rnd(), tint, scale)
            : stump(rnd(), scale)
    p.position.set(x, 0, z)
    p.rotation.y = rnd() * Math.PI * 2
    const q = quads[half(x, z)]
    q?.add(p)
    if (q !== undefined) blobShadow(q, x, z, spread * 0.72)
    placed.push({ x0: x - spread, z0: z - spread, x1: x + spread, z1: z + spread })
    made++
  }
  for (const p of placed) blockers.push(p)

  /*
   * Distant tree line + hills: pure skyline, never walked to, never shadowed.
   *
   * The far ring is now SPACED and CLEARED as well. Round 1 dropped 60 trees on
   * a ring with no overlap test at all, so they grew through each other and
   * straight through the cottages' roofs — the cottage ring and the tree ring
   * were at the same radius. `blockers` already contains every cottage
   * footprint by the time this runs, so testing against it is free.
   */
  /*
   * AERIAL PERSPECTIVE, EXPLICITLY.
   *
   * Round 2: "pale white/grey quads float above the canopy … the ground fades to
   * a dark green at the top with a vertical gradient and no horizon, no sky, no
   * fog, so the map simply runs out."
   *
   * Both symptoms were the same cause. Distance fog on an ORTHOGRAPHIC camera is
   * measured from the camera plane 60 units back, so the far hills sat past the
   * fog's far plane and came out as flat, fully-fogged pale shapes — the
   * "floating quads" — while the ground between them, which was inside the fog's
   * near plane, stayed a hard dark green with no gradation at all.
   *
   * Distance is now painted rather than fogged: each ring of the skyline is
   * pre-mixed toward the sky colour by hand, so the tree line, the hills and the
   * ground bands all step evenly from woodland green to haze. That is
   * controllable, cheap, and it works at every zoom the camera has.
   */
  const farPlaced: Rect[] = []
  let farTries = 0
  let farMade = 0
  while (farMade < 34 && farTries < 1400) {
    farTries++
    const a = rnd() * Math.PI * 2
    const rad = GREEN_BELT_OUT + 2 + rnd() * 13
    const px = Math.cos(a) * rad
    const pz = Math.sin(a) * rad
    if (hits(px, pz, blockers, 3.0) || hits(px, pz, farPlaced, 2.9)) continue
    // Haze grows with distance: 0 at the belt, ~0.55 at the last tree line.
    // Snapped to four steps so the whole far ring is a few merged meshes.
    const haze = quant(Math.min(0.6, ((rad - GREEN_BELT_OUT) / 15) * 0.5 + rnd() * 0.12), 2)
    /*
     * AERIAL PERSPECTIVE TOWARD THE BACKDROP, NOT TOWARD THE SKY.
     *
     * Was `mix('leafDark','skyHorizon', haze)`. The intent was right and the
     * reference was wrong: the sky is not what is behind a tree at radius 45 —
     * the painted haze BAND is, and that band is already most of the way to the
     * sky. Mixing all the way to `skyHorizon` therefore overshot its own
     * backdrop, and at haze 0.5 the far trees came out PALER than the ground
     * they stand on. Fading toward `groundAt(rad)` fades exactly as far as the
     * distance warrants, and `underGround` then guarantees the tree line is a
     * step below the band whatever the haze — which is what makes a horizon read
     * as trees rather than as noise, at noon and at two in the morning alike.
     */
    const back = groundAt(rad)
    const tint = underGround(blend(mix('leafDark', 'leaf', 0.25), back, haze), back)
    const sc = 0.85 + rnd() * 0.75
    const p = rnd() > 0.42 ? pineTree(rnd(), true, tint, sc) : tree(rnd(), tint, sc)
    p.position.set(px, -0.3, pz)
    p.traverse((o) => {
      const m = o as THREE.Mesh
      if (m.isMesh === true) {
        m.castShadow = false
        m.receiveShadow = false
      }
    })
    far.add(p)
    farPlaced.push({ x0: px - 1.8 * sc, z0: pz - 1.8 * sc, x1: px + 1.8 * sc, z1: pz + 1.8 * sc })
    farMade++
  }
  // Two hill lines. The near one still reads as land; the far one is nearly the
  // sky colour, which is what turns "the map runs out" into a horizon.
  for (let band = 0; band < 2; band++) {
    const n = band === 0 ? 10 : 12
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + 0.3 + band * 0.42
      const rad = band === 0 ? 50 + rnd() * 7 : 60 + rnd() * 7
      const hx = Math.cos(a) * rad
      const hz = Math.sin(a) * rad
      const haze = band === 0 ? quant(0.42 + rnd() * 0.12, 1) : quant(0.68 + rnd() * 0.12, 1)
      // Same rule as the tree line: a hill fades toward the band behind it and
      // stays a step below it, so the horizon still reads as land after dark.
      const back = groundAt(rad)
      const h = hill(
        (band === 0 ? 9 : 12) + rnd() * 8,
        (band === 0 ? 5 : 6) + rnd() * 6,
        underGround(blend(hex(rnd() > 0.5 ? 'grassDark' : 'leafDark'), back, haze), back),
      )
      h.position.set(hx, -1.5, hz)
      far.add(h)
    }
  }
  for (let i = 0; i < quads.length; i++) {
    const near = quads[i]
    if (near !== undefined && near.children.length > 0) {
      statics.add(mergeStatic(near, `greenery${i}`, { castShadow: false }))
    }
  }
  statics.add(mergeStatic(far, 'skyline', { castShadow: false, receiveShadow: false }))
}

/** Lamp posts, roadside flowers and the gate signs. */
function streetFurniture(statics: THREE.Object3D, taps: SceneryTap[]): THREE.Mesh | null {
  const g = group('streetFurniture')
  const lampR = (YARD_HALF + ROAD_IN) / 2
  /*
   * LAMPS HAVE A RULE NOW: one at each end of each verge, flanking the crossing.
   *
   * Round 2: "six identical red-capped lamp posts at even spacing, one planted
   * in the grass beside a flower bed and one on the pavement with no rule".
   * Even spacing with no relationship to anything IS the defect — street lights
   * in a real town mark the junctions and the crossing. Twelve became eight, all
   * of them on the pavement, all of them doing a job a kid can see: they are the
   * lights that come on when you tap one.
   */
  for (const s of [-1, 1]) {
    for (const t of [-GATE_HALF - 2.2, GATE_HALF + 2.2]) {
      const a = lampPost()
      a.position.set(t, 0, s * lampR)
      g.add(a)
      blobShadow(g, t, s * lampR, 0.5)
      taps.push(lampTap(`lamp:${t}:${s}z`, t, s * lampR, s))
      const b = lampPost()
      b.position.set(s * lampR, 0, t)
      g.add(b)
      blobShadow(g, s * lampR, t, 0.5)
      taps.push(lampTap(`lamp:${s}x:${t}`, s * lampR, t, s))
    }
  }
  // flowers hugging the plot fence, skipping the gates
  const rnd = seededRandom(4242)
  for (let t = -YARD_HALF + 1.6; t < YARD_HALF - 1; t += 2.8) {
    if (Math.abs(t) < GATE_HALF + 1.2) continue
    for (const s of [-1, 1]) {
      const colors: PaletteName[] = ['flowerPink', 'flowerYellow', 'flowerPurple', 'flowerRed']
      const c = colors[Math.floor(rnd() * colors.length)] ?? 'flowerPink'
      const f1 = flowerClump(rnd(), c)
      f1.position.set(t, 0, s * (YARD_HALF + 0.85))
      g.add(f1)
      taps.push(flowerTap(`bloom:${t}:${s}z`, t, s * (YARD_HALF + 0.85), 0, -s))
      const f2 = flowerClump(rnd(), c)
      f2.position.set(s * (YARD_HALF + 0.85), 0, t)
      g.add(f2)
      taps.push(flowerTap(`bloom:${s}x:${t}`, s * (YARD_HALF + 0.85), t, -s, 0))
    }
  }
  /*
   * Picture signs at each gate, CLEAR OF THE CROSSING.
   *
   * Round 2 found "a signboard planted in the middle of the pedestrian crossing"
   * in two different frames. The signs were placed at GATE_HALF + 1.5 with a
   * ±1.0 offset off the fence line, which put them on the verge but inside the
   * crossing's own corridor once the crossing widened. They now sit a clear 3.4
   * units to the side of the gate centreline — beside the crossing, facing the
   * road, which is where a real signpost goes.
   */
  const signOff = GATE_HALF + 3.4
  const verge = (YARD_HALF + ROAD_IN) / 2
  const signs: readonly [number, number, number, 'home' | 'cone' | 'coin' | 'tree'][] = [
    [signOff, -verge, 0, 'home'],
    [-signOff, verge, Math.PI, 'cone'],
    [verge, signOff, Math.PI / 2, 'coin'],
    [-verge, -signOff, -Math.PI / 2, 'tree'],
  ]
  for (const [x, z, yaw, icon] of signs) {
    const s = signPost(icon)
    s.position.set(x, 0, z)
    s.rotation.y = yaw
    g.add(s)
    blobShadow(g, x, z, 0.55)
    taps.push({
      id: `sign:${icon}`,
      at: { x, y: 2.5, z },
      standAt: { x: x + Math.sin(yaw) * 1.8, y: 0, z: z + Math.cos(yaw) * 1.8 },
      radius: 2.2,
      icon,
      cue: 'wood_clack',
      effect: 'sparkle',
      action: 'think',
    })
  }
  // Street furniture keeps its shadows: the lamp posts' long shadows across the
  // road are most of what makes the street read as daylight, and it is one
  // cluster of a dozen meshes.
  const merged = mergeStatic(g, 'streetFurniture')
  statics.add(merged)

  /*
   * Find the merged lamp-bulb mesh. Every bulb uses the same unlit `sunWarm`
   * material, so merging collected all twelve of them into exactly one mesh —
   * which means "turn the town's lights on" is a single material swap and a
   * single draw call, not twelve pickable props an older iPad cannot afford.
   */
  const glowMat = flat('sunWarm')
  let glow: THREE.Mesh | null = null
  merged.traverse((o) => {
    const m = o as THREE.Mesh
    if (m.isMesh === true && m.material === glowMat) glow = m
  })
  return glow
}

function lampTap(id: string, x: number, z: number, side: number): SceneryTap {
  return {
    id,
    at: { x, y: 2.9, z },
    standAt: { x: x - side * 1.5, y: 0, z: z - side * 1.5 },
    radius: 2.1,
    icon: 'light',
    cue: 'light_on',
    effect: 'sparkle',
    action: 'wave',
    lamp: true,
  }
}

function flowerTap(id: string, x: number, z: number, ox: number, oz: number): SceneryTap {
  return {
    id,
    at: { x, y: 0.55, z },
    standAt: { x: x + ox * 1.4, y: 0, z: z + oz * 1.4 },
    radius: 1.7,
    icon: 'plant',
    cue: 'flower_smell',
    effect: 'petals',
    action: 'sniff',
  }
}

// ---------------------------------------------------------------------------
// grid hint
// ---------------------------------------------------------------------------

function gridHint(node: SceneNode): THREE.Object3D {
  const g = node.grid
  const t = g.tileSize
  const lines = group('gridHint')
  /*
   * THE SNAP GRID IS A HINT, NOT A DEBUG OVERLAY.
   *
   * Round 2 read it as exactly that: "a faint grey debug grid is visible across
   * the entire field … still visible over the grass and plaza in build mode".
   * Two changes. It is now confined to THE KID'S FENCED YARD — the only place a
   * kid starts building, and the place the fence already says is theirs — rather
   * than stretched over the whole 48x48 grid including the road, the pond and
   * the neighbours' gardens. And it is fainter, so it reads as a guide under the
   * grass rather than a wireframe over it.
   */
  const mat = new THREE.LineBasicMaterial({
    color: hex('flowerWhite'),
    transparent: true,
    opacity: 0.26,
    depthWrite: false,
  })
  const pts: number[] = []
  const lo = -YARD_HALF
  const hi = YARD_HALF
  const first = Math.ceil((lo - g.originX) / t)
  const last = Math.floor((hi - g.originX) / t)
  for (let i = first; i <= last; i++) {
    const x = g.originX + i * t
    pts.push(x, 0.12, lo, x, 0.12, hi)
  }
  const firstZ = Math.ceil((lo - g.originZ) / t)
  const lastZ = Math.floor((hi - g.originZ) / t)
  for (let i = firstZ; i <= lastZ; i++) {
    const z = g.originZ + i * t
    pts.push(lo, 0.12, z, hi, 0.12, z)
  }
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3))
  lines.add(new THREE.LineSegments(geo, mat))
  return lines
}

// ---------------------------------------------------------------------------
// the town
// ---------------------------------------------------------------------------

export function buildTown(node: SceneNode): TownBuild {
  const blockers: Rect[] = []
  const buildings: BuiltBuilding[] = []
  const landmarks: Landmark[] = []
  const taps: SceneryTap[] = []

  // ---- ground -----------------------------------------------------------
  // The backdrop is split out from the rest of the ground on purpose: it is the
  // one mesh that covers the entire screen, and having it sample a soft shadow
  // map per pixel was the most expensive single thing in the town.
  const backdrop = group('townBackdrop')
  /*
   * The field's speckle is drawn on a NEAR-WHITE base, not on `grassDark`.
   *
   * A texture map MULTIPLIES the material colour, so a speckle whose base is
   * `grassDark` darkened the whole field by that much — which is why the field
   * disc read as a dark green bullseye against the first (untextured) haze band
   * beyond it. A near-white base leaves the field exactly the colour it is
   * declared to be, and the flecks still land.
   */
  const wild = speckleTexture('flowerWhite', 'grassLight', 110)
  wild.repeat.set(26, 26)
  /*
   * THE FIELD AND THE HORIZON.
   *
   * Was: one 132x132 square of flat `grass`, which is why the establishing shot
   * ended in a dark green wall with "no horizon, no sky, no fog … the map simply
   * runs out". The near field is still one textured plane (it is the cheapest
   * thing on screen and the town sits on it), but beyond it the ground now steps
   * out through five painted bands that fade to the sky colour, so the world
   * dissolves into haze instead of stopping at an edge. The last band IS
   * `skyHorizon`, the same colour as the sky gradient behind it, so wherever the
   * camera reaches past the ground there is no seam to see.
   *
   * Warm, not kelly: the near field is nudged toward `sand`. The round-2 note
   * was "the town's grass is a hard saturated green that fights the pastel UI";
   * this is the biggest single colour area in the game and it sets the key.
   */
  // A DISC, not a square: the bands beyond it are concentric, and a square field
  // would poke its four corners of full-saturation green out through the haze.
  const fieldGeo = new THREE.CircleGeometry(FIELD, 64)
  fieldGeo.rotateX(-Math.PI / 2)
  const field = new THREE.Mesh(fieldGeo, groundMaterial(FIELD_GREEN, wild))
  field.position.y = -0.04
  field.receiveShadow = false
  field.castShadow = false
  backdrop.add(field)
  /*
   * THE APRON — the one bit of the outer field that RECEIVES a shadow.
   *
   * The four street buildings stand between radius 20 and 26, which is outside
   * the painted town ground and therefore on the big field plane. That plane has
   * `receiveShadow = false` for a very good reason (it covers the whole screen,
   * and sampling a soft shadow map over every one of those pixels was measured
   * as the most expensive single thing the town does) — but the consequence was
   * the round-2 blocker: the buildings cast into a surface that refuses to show
   * it, so "the pink ice-cream shop and the red house cast NO shadow on the
   * grass while the lamp posts, bench and tree next to them cast long ones."
   *
   * A 13-unit-wide ring under the buildings receives; the other ~85% of the
   * field still does not. Same shadows, a twelfth of the fill.
   */
  ringBand(backdrop, WALK_OUT - 0.6, 34, -0.035, FIELD_GREEN, true, wild, FIELD)
  /*
   * THE HAZE LADDER, EVENLY SPACED AND ON THE SAME SURFACE.
   *
   * Two round-3 notes land here. The first band used to jump from haze 0.04
   * straight to 0.28, which is a bigger step than any of the five above it and
   * reads as a hard ring right where the eye is looking — the reported
   * "banding". And every band was untextured while the field they continue was
   * textured, so the first one changed grain as well as tone. Eight even-ish
   * steps on the SAME textured material step out to the sky with nothing to
   * catch on, and cost the same number of draw calls each (one bucket per
   * colour, exactly as before).
   */
  const bands: readonly [number, number, number][] = [
    [FIELD - 0.5, 48, 0.07],
    [48, 53, 0.16],
    [53, 58, 0.27],
    [58, 64, 0.39],
    [64, 71, 0.52],
    [71, 80, 0.67],
    [80, 92, 0.83],
    [92, 108, 0.96],
  ]
  for (const [inner, outer, haze] of bands) {
    ringBand(backdrop, inner, outer, -0.05, blend(FIELD_GREEN, hex('skyHorizon'), haze), false, wild, FIELD)
  }

  const ground = group('townGround')
  const lawn = lawnTexture()
  lawn.repeat.set(4, 4)
  slab(ground, -YARD_HALF, -YARD_HALF, YARD_HALF, YARD_HALF, 0, mix('grassLight', 'sand', 0.14), lawn)
  plaza(ground)

  // pavement / road / pavement. The kerb is drawn as two thin bands rather than
  // one big slab under everything — that slab was ~1900 units of pure overdraw.
  /*
   * THE DRAWN EDGE, on both sides of the ring.
   *
   * `paved()` lays a soft ink band under every kerb it draws, which is what
   * stops a path reading as one flat colour cut into another. The ring road was
   * built out of raw `annulus()` bands and never got one, so its outer boundary
   * was a bare kerb meeting bare grass — the round-3 "hard cut edge" at the town
   * boundary. GROUND_INK already exists, so this is two more bands in a bucket
   * the merge is already paying for.
   */
  annulus(ground, YARD_HALF - 0.075, YARD_HALF, 0.016, GROUND_INK)
  annulus(ground, WALK_OUT + 0.5, WALK_OUT + 0.575, 0.016, GROUND_INK)
  annulus(ground, YARD_HALF, YARD_HALF + 0.4, 0.02, 'pathEdge')
  annulus(ground, WALK_OUT - 0.05, WALK_OUT + 0.5, 0.02, 'pathEdge')
  annulus(ground, YARD_HALF + 0.35, ROAD_IN, 0.04, 'path')
  annulus(ground, ROAD_OUT, WALK_OUT, 0.04, 'path')
  annulus(ground, ROAD_IN, ROAD_OUT, 0.06, 'road')
  roadMarkings(ground, 0.08)
  crossings(ground, 0.09)
  // the four gate paths, joining the plot to the crossings — kerbed like the
  // rest, so the grass edge is a two-step transition and not a raw staircase
  for (const s of [-1, 1]) {
    const a = s * (YARD_HALF - 1.6)
    const b = s * (ROAD_IN + 0.3)
    paved(ground, -GATE_HALF, Math.min(a, b), GATE_HALF, Math.max(a, b), 0.07, 'path')
    paved(ground, Math.min(a, b), -GATE_HALF, Math.max(a, b), GATE_HALF, 0.07, 'path')
  }
  /*
   * Three lanes out of town, at three different offsets, each clear of the
   * buildings and the pond: north past the west end of the house, east past the
   * shop, west past the pond's south shore. See `exitLane`.
   */
  exitLane(ground, blockers, 'x', -10.6, -1)
  exitLane(ground, blockers, 'z', 10.6, 1)
  exitLane(ground, blockers, 'z', -12.2, -1)
  /*
   * THE PAINTED TOWN IS ITS OWN GROUP, so "show me the empty plot" is real.
   *
   * `clearTown()` hid the baked statics but not the ground, on the reasoning
   * that an empty plot is grass and not a void. True — but every road, kerb,
   * crossing, gate path, plaza and pond ring is ALSO ground, so the state a
   * critic loop kept trying to review still came back as a complete town with
   * the props taken off it. Splitting the paint from the grass under it costs
   * nothing (a group with no transform) and makes the bare plot capturable:
   * the wild-grass backdrop and the snap grid stay, everything painted goes.
   */
  const paint = group('townPaint')
  paint.add(mergeStatic(ground, 'townGround'))
  node.ground.add(paint)
  // The 132x132 backdrop plane covers the whole screen; sampling a soft shadow
  // map over every one of those pixels is the single most expensive thing the
  // town can do, and nothing out there casts a shadow anyway.
  node.ground.add(mergeStatic(backdrop, 'townBackdrop', { castShadow: false, receiveShadow: false }))

  const hint = gridHint(node)
  node.ground.add(hint)

  // ---- fence ------------------------------------------------------------
  plotFence(node.statics, blockers)

  // ---- buildings --------------------------------------------------------
  for (const spec of BUILDINGS) {
    const built = makeBuilding(spec)
    // Merging consumes the source group, so the MERGED group is what a tap
    // makes bounce.
    const art = mergeStatic(built.art, `building:${spec.id}`)
    node.statics.add(art)
    buildings.push(built)
    blockers.push(built.blocker)
    landmarks.push({
      id: `door:${spec.id}`,
      at: built.door,
      standAt: built.standAt,
      radius: 4.2,
      art,
      ...(spec.toScene === undefined ? {} : { toScene: spec.toScene }),
      ...(spec.panel === undefined ? {} : { panel: spec.panel }),
      icon: spec.sign === 'none' ? 'door' : spec.sign,
      cue: 'door_knock',
      effect: 'sparkle',
    })
  }
  /*
   * Cottages are pure scenery, so they ride along in the quadrant clusters —
   * four separate clusters cost forty draw calls for buildings nobody enters.
   *
   * EACH ONE GETS A YARD. Round 1 dropped them on bare grass with nothing
   * around them and no route to anywhere, and the critic read them as orphans
   * that had fallen out of the town. A cottage now comes with a garden path off
   * its front door, a hedge line and a pair of bushes — the same cues that make
   * the kid's own plot read as somewhere someone lives.
   */
  // ONE cottage cluster. Four cost four copies of the same eleven material
  // buckets for four buildings nobody enters; see the note in `scatterGreenery`.
  const cottageQuads = [group('cot0')]
  for (const c of COTTAGES) {
    const built = cottage(c.x, c.z, c.wall, c.roof, c.yaw)
    const q = cottageQuads[0]
    q?.add(built.art)
    blockers.push(built.blocker)
    // Front door faces +Z in local space, so the yard runs down the cottage's
    // own forward axis and turns toward town.
    const fx = Math.sin(c.yaw)
    const fz = Math.cos(c.yaw)
    const yard = group('cottageYard')
    for (let i = 0; i < 5; i++) {
      const px = c.x + fx * (3.2 + i * 1.5)
      const pz = c.z + fz * (3.2 + i * 1.5)
      slab(ground, px - 1.1, pz - 1.1, px + 1.1, pz + 1.1, 0.02, 'pathEdge')
      slab(ground, px - 0.82, pz - 0.82, px + 0.82, pz + 0.82, 0.024, 'path')
    }
    /*
     * HEDGES ONLY. A bush and a tree per cottage looked lovely and cost about
     * five extra draw calls per quadrant cluster, because `mergeStatic` buckets
     * by material and every leaf shade is its own bucket. The tree line already
     * runs right behind these cottages, so the greenery was redundant; the
     * hedge and the garden path are what actually say "somebody lives here".
     */
    for (const s of [-1, 1]) {
      const h = hedge(3.4, 0.5)
      h.position.set(c.x + fx * 3.0 - fz * s * 3.4, 0, c.z + fz * 3.0 + fx * s * 3.4)
      h.rotation.y = c.yaw
      yard.add(h)
    }
    q?.add(yard)
  }
  for (let i = 0; i < cottageQuads.length; i++) {
    const q = cottageQuads[i]
    if (q !== undefined && q.children.length > 0) {
      node.statics.add(mergeStatic(q, `cottages${i}`, { castShadow: false }))
    }
  }

  // ---- pond, gardens, greenery, street furniture -------------------------
  landmarks.push(buildPond(paint, node.statics, blockers))
  const gardens = group('cornerGardens')
  cornerGardens(gardens, blockers, taps)
  node.statics.add(mergeStatic(gardens, 'cornerGardens', { castShadow: false }))
  plazaFountain(node.statics, blockers, taps)
  scatterGreenery(node.statics, blockers)
  const lampGlow = streetFurniture(node.statics, taps)

  /*
   * ---- THE PARK STRIP ---------------------------------------------------
   * The four lawns inside the fence are the kid's canvas and stay empty on
   * purpose. The band BETWEEN the fence and the pavement was empty too, and
   * that is what made the critic's frame read as "four large blank lawns" — a
   * town with nothing living in the gaps. Benches and shade trees go there:
   * outside the buildable grid, on the walk, where a monster can sit.
   */
  const park = group('parkStrip')
  const parkR = (YARD_HALF + ROAD_IN) / 2
  const seats: readonly [number, number, number][] = [
    [-6.5, -parkR, 0],
    [6.5, parkR, Math.PI],
    [parkR, -6.5, -Math.PI / 2],
    [-parkR, 6.5, Math.PI / 2],
  ]
  for (const [x, z, yaw] of seats) {
    const b = bench()
    b.position.set(x, 0, z)
    b.rotation.y = yaw
    park.add(b)
    blobShadow(park, x, z, 1.35)
    blockers.push(benchBlocker(x, z, yaw))
    taps.push({
      id: `bench:${x}:${z}`,
      at: { x, y: 0.6, z },
      standAt: { x: x + Math.sin(yaw) * 1.5, y: 0, z: z + Math.cos(yaw) * 1.5 },
      radius: 2.2,
      icon: 'furniture',
      cue: 'bench_creak',
      effect: 'poof',
      action: 'sit',
    })
  }
  // Shade trees sit at the ring's CORNERS, where the verge is widest, and are
  // scaled down: a full-size 4.2-unit crown on the straight sections hangs over
  // the crossing and the shop fronts behind it.
  const shade: readonly [number, number][] = [
    [-11.4, -parkR + 0.15],
    [11.4, parkR - 0.15],
    [parkR - 0.15, -11.4],
    [-parkR + 0.15, 11.4],
  ]
  const parkRnd = seededRandom(5150)
  for (const [x, z] of shade) {
    // 0.7, not 0.82: a 2.7-unit crown on a 1.85-unit verge still hangs over the
    // kerb, and a shade tree that overhangs the road is the same defect as a
    // tree planted on it. The scale goes THROUGH the factory so the ink weight
    // matches the full-size trees behind it — see the note in `scatterGreenery`.
    const t = tree(0.15 + parkRnd() * 0.3, undefined, 0.7)
    t.position.set(x, 0, z)
    t.rotation.y = parkRnd() * Math.PI
    park.add(t)
    blobShadow(park, x, z, 1.3)
    blockers.push({ x0: x - 1.1, z0: z - 1.1, x1: x + 1.1, z1: z + 1.1 })
    taps.push({
      id: `tree:${x}:${z}`,
      at: { x, y: 2.4, z },
      standAt: { x: x * 0.82, y: 0, z: z * 0.82 },
      radius: 2.4,
      icon: 'tree',
      cue: 'leaves_rustle',
      effect: 'leaves',
      action: 'hop',
    })
  }
  // castShadow OFF (see merge.ts): a merged cluster's bounding sphere is huge,
  // so it is almost never culled out of the shadow map and pays a full extra
  // pass every frame. The buildings and the lamp posts carry the town's
  // shadows; verge trees and benches do not need to.
  node.statics.add(mergeStatic(park, 'parkStrip', { castShadow: false }))

  // ---- the mailbox by the front path ------------------------------------
  const post = mailbox()
  post.position.set(-GATE_HALF - 1.4, 0, -YARD_HALF - 0.9)
  post.rotation.y = 0
  node.statics.add(post)
  landmarks.push({
    id: 'mailbox',
    at: { x: -GATE_HALF - 1.4, y: 1.4, z: -YARD_HALF - 0.9 },
    standAt: { x: -GATE_HALF - 1.4, y: 0, z: -YARD_HALF + 1.4 },
    radius: 2.6,
    art: post,
    icon: 'gift',
    cue: 'mail_open',
    effect: 'stars',
  })

  // The world beyond the town is scenery, not somewhere to walk.
  blockers.push({ x0: -300, z0: -300, x1: 300, z1: -GRID_HALF })
  blockers.push({ x0: -300, z0: GRID_HALF, x1: 300, z1: 300 })
  blockers.push({ x0: -300, z0: -300, x1: -GRID_HALF, z1: 300 })
  blockers.push({ x0: GRID_HALF, z0: -300, x1: 300, z1: 300 })

  // The plaza itself answers a tap — it is the biggest thing on the plot and
  // the one a kid's finger lands on by accident most often.
  taps.push({
    id: 'plaza',
    at: { x: 0, y: 0.1, z: 0 },
    standAt: { x: 0, y: 0, z: 1.6 },
    radius: 2.2,
    icon: 'star',
    cue: 'step_stone',
    effect: 'confetti',
    action: 'spin',
  })

  return { blockers, buildings, landmarks, taps, lampGlow, gridHint: hint, paint }
}

/**
 * Sky gradient. The camera is orthographic, so a sky DOME cannot work (anything
 * outside the ortho box is clipped) — the gradient has to be the scene
 * background. One texture, zero draw calls, and the flat blue wall of the stub
 * build turns into warm daylight.
 */
export function skyTexture(): THREE.Texture {
  const tex = generatedTexture('sky:day', (c, s) => {
    const grd = c.createLinearGradient(0, 0, 0, s)
    grd.addColorStop(0, css('skyTop'))
    grd.addColorStop(0.55, PALE_SKY)
    grd.addColorStop(1, css('skyHorizon'))
    c.fillStyle = grd
    c.fillRect(0, 0, s, s)
  })
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  return tex
}
